import type { Photo, FinalStatus, ApprovalStatus, ArtistStatus } from '../../types';
import { isResolvedCredit, type ResolvedCredit } from '../../utils/photoCredits';
import {
    approvalBadgeClass,
    creditBadgeClass,
    photoStatusTooltip,
    formatCountdown,
    notifShortLabel,
    notifTooltip,
    timerTooltip,
    creditStatusTooltip,
} from '../../utils/approvalStatus';

interface Props {
    photo: Photo;
    photoStatusMap: Map<number, FinalStatus>;
    photoCreditsMap: Map<string, ApprovalStatus>;
    artistStatusMap: Map<number, ArtistStatus>;
    batchIsCommitted: boolean;
    canCorrectTags?: boolean;
    onCorrectTags?: (photo: Photo) => void;
    exportMode?: boolean;
    isExportSelected?: boolean;
    onExportToggle?: (id: number) => void;
}

export default function ApprovalPhotoCard({
    photo,
    photoStatusMap,
    photoCreditsMap,
    artistStatusMap,
    batchIsCommitted,
    canCorrectTags = false,
    onCorrectTags,
    exportMode = false,
    isExportSelected = false,
    onExportToggle,
}: Props) {
    const status = photoStatusMap.get(photo.id) ?? photo.finalStatus ?? 'raw';
    const isUnselected = !photo.isSelected;
    const credits = (photo.credits ?? []).filter(
        (c): c is typeof c & { userId: ResolvedCredit } => isResolvedCredit(c.userId)
    );

    const cardClass = [
        'photo-card',
        isUnselected ? 'photo-card-unselected' : '',
        exportMode && isExportSelected ? 'photo-card-export-selected' : '',
        exportMode ? 'photo-card-export-mode' : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={cardClass}
            onClick={exportMode && onExportToggle ? () => onExportToggle(photo.id) : undefined}
        >
            <div className='photo-img-wrapper'>
                <img src={photo.thumbnailURL ?? photo.displayURL} alt={photo.originalFilename} />
                {exportMode && (
                    <span className={`export-checkbox-indicator${isExportSelected ? ' checked' : ''}`}>
                        {isExportSelected ? '✓' : ''}
                    </span>
                )}
                {batchIsCommitted && !isUnselected && (
                    <span className={`photo-status-badge ${approvalBadgeClass(status)}`} title={photoStatusTooltip(status)}>
                        {status}
                    </span>
                )}
            </div>

            <div className='photo-meta'>
                <div className='photo-meta-row'>
                    <span className='photo-filename' title={photo.originalFilename}>
                        {photo.originalFilename}
                    </span>
                    {isUnselected && (
                        <span className='badge badge-unselected'>Not Selected</span>
                    )}
                    {!batchIsCommitted && !isUnselected && (
                        <span className={approvalBadgeClass(status)} title={photoStatusTooltip(status)}>{status}</span>
                    )}
                    {batchIsCommitted && !isUnselected && canCorrectTags && !photo.isGroupPhoto && onCorrectTags && (
                        <button
                            className='correct-tags-btn'
                            type='button'
                            onClick={e => { e.stopPropagation(); onCorrectTags(photo); }}
                        >
                            Correct Tags
                        </button>
                    )}
                </div>

                {!batchIsCommitted && (credits.length > 0 || photo.isGroupPhoto) && (
                    <div className='photo-tags'>
                        {photo.isGroupPhoto && (
                            <span className='photo-tag-pill group'>Group Photo</span>
                        )}
                        {credits.map((c, i) => (
                            <span key={i} className='photo-tag-pill'>
                                {c.userId.fName} {c.userId.lName}
                            </span>
                        ))}
                    </div>
                )}

                {batchIsCommitted && !isUnselected && (
                    <div className='photo-credit-rows'>
                        {photo.isGroupPhoto && (
                            <div className='photo-credit-row'>
                                <span className='photo-credit-name'>Group Photo</span>
                            </div>
                        )}
                        {credits.map((credit, i) => {
                            const userId = credit.userId.id;
                            const artist = artistStatusMap.get(userId);
                            const name = `${credit.userId.fName} ${credit.userId.lName}`;
                            const notifStatus = artist?.notificationStatus ?? null;
                            const timer = artist?.timer ?? { state: 'not_started' as const, endTime: null };
                            const isNotNotified = (notifStatus === 'failed' || notifStatus === 'bounced') && timer.state === 'not_started';
                            const liveStatus = photoCreditsMap.get(`${photo.id}-${userId}`) ?? credit.approvalStatus;
                            return (
                                <div key={i} className='photo-credit-row'>
                                    <span className='photo-credit-name'>{name}</span>
                                    <span className='photo-credit-sep'>·</span>
                                    <span
                                        className={`photo-credit-notif${(notifStatus === 'failed' || notifStatus === 'bounced') ? ' photo-credit-notif-failed' : ''}`}
                                        title={notifTooltip(notifStatus)}
                                    >
                                        {notifShortLabel(notifStatus)}
                                    </span>
                                    <span className='photo-credit-sep'>·</span>
                                    <span className='photo-credit-timer' title={timerTooltip(timer.state)}>
                                        {formatCountdown(timer.endTime, timer.state)}
                                    </span>
                                    <span className='photo-credit-sep'>·</span>
                                    {isNotNotified ? (
                                        <span className='credit-badge credit-badge-not-notified' title='Artist has not been notified yet'>Not notified</span>
                                    ) : (
                                        <span className={creditBadgeClass(liveStatus)} title={creditStatusTooltip(liveStatus)}>{liveStatus}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}